import { z } from "zod";
import type { Role } from "./types.js";
import { senhaZod } from "./signup.js";

// =====================================================================
// Permissões por GRUPO e por USUÁRIO (out/2026, D-27).
//
// Antes: o papel (`role`) decidia tudo, com listas fixas no código. A Brasil
// Novo pediu perfis próprios ("acesso total", "gestor de frota", "analista")
// e que quem tem acesso total possa ajustar o que cada GRUPO ou cada PESSOA
// enxerga, sem deploy. Por isso a autorização agora pergunta por uma
// PERMISSÃO (capacidade nomeada), e o papel virou só o rótulo legado.
//
// Permissões efetivas = (do grupo ∪ concedidas ao usuário) − retiradas do
// usuário. Usuário sem grupo (sessão antiga, conta de seed fora de grupo)
// cai no mapa ROLE_DEFAULT_PERMISSIONS, que reproduz exatamente o RBAC
// anterior — ninguém ganha nem perde acesso na virada.
// =====================================================================

export interface PermissionSpec {
  key: string;
  label: string;
  description: string;
  category: string;
}

export const PERMISSIONS = [
  // --- Acompanhamento -------------------------------------------------
  { key: "paineis", category: "Acompanhamento", label: "Painéis de acompanhamento",
    description: "Início, Torre de Controle (leitura), Painel de carregamentos, visão planilha e Indicadores." },
  { key: "operacoes", category: "Acompanhamento", label: "Tabela de operações",
    description: "Tabela completa de viagens com histórico de alterações." },
  { key: "feeds", category: "Acompanhamento", label: "Feeds",
    description: "O que mudou em cada fonte de dados." },
  // --- Operação --------------------------------------------------------
  { key: "vinculo", category: "Operação", label: "Vínculo de cargas",
    description: "Vincular carga: escolher motorista e placas e solicitar data e hora da coleta." },
  { key: "agendamento", category: "Operação", label: "Agendamentos",
    description: "Confirmar ou alterar o horário de coleta, editar cargas, criar ordem de coleta, registrar disponibilidade de motorista e importar planilhas." },
  { key: "programacao", category: "Operação", label: "Programação",
    description: "Consultar a programação e o histórico de importações." },
  // --- Cadastros -------------------------------------------------------
  { key: "cadastros", category: "Cadastros", label: "Ver cadastros",
    description: "Veículos, motoristas, tarifas e regiões (leitura)." },
  { key: "cadastros.frota", category: "Cadastros", label: "Editar cadastros de frota",
    description: "Veículos, PBTC, cursos e exames periódicos." },
  { key: "cadastros.comercial", category: "Cadastros", label: "Editar cadastros comerciais",
    description: "Clientes, tarifas e regiões." },
  { key: "pii", category: "Cadastros", label: "Ver dados pessoais",
    description: "Nome completo e contato de motoristas nas respostas do agente de IA." },
  // --- Resultados ------------------------------------------------------
  { key: "metas", category: "Resultados", label: "Metas e insights",
    description: "Metas, Insights, Inconsistências, Explorar com IA e Agente IA." },
  { key: "metas.editar", category: "Resultados", label: "Editar metas",
    description: "Alterar e recalcular metas por região." },
  { key: "dados", category: "Resultados", label: "Explorar dados",
    description: "Tabelas das fontes e inconsistências." },
  { key: "dados.editar", category: "Resultados", label: "Corrigir dados das fontes",
    description: "Editar registros na tela Explorar dados." },
  // --- Administração ---------------------------------------------------
  { key: "auditoria", category: "Administração", label: "Auditoria",
    description: "Trilha de quem alterou o quê." },
  { key: "chamados.triagem", category: "Administração", label: "Triagem de chamados",
    description: "Ver a fila inteira de chamados, priorizar e atribuir." },
  { key: "usuarios", category: "Administração", label: "Gerenciar usuários e permissões",
    description: "Criar, editar e excluir usuários, grupos e permissões." },
  { key: "sistema", category: "Administração", label: "Administração técnica",
    description: "Sincronizar APIs, reindexar busca, excluir chamados e acompanhar a entrega do projeto." },
] as const satisfies readonly PermissionSpec[];

export type Permission = (typeof PERMISSIONS)[number]["key"];
export const PERMISSION_KEYS = PERMISSIONS.map((p) => p.key) as Permission[];

export const isPermission = (v: unknown): v is Permission =>
  typeof v === "string" && (PERMISSION_KEYS as string[]).includes(v);

/** Permissões ordenadas pelo catálogo, sem duplicatas nem chaves desconhecidas. */
export function normalizePermissions(list: readonly unknown[]): Permission[] {
  const set = new Set(list.filter(isPermission));
  return PERMISSION_KEYS.filter((k) => set.has(k));
}

/** Catálogo agrupado por categoria, na ordem de declaração (alimenta a tela). */
export function permissionCategories(): { category: string; items: PermissionSpec[] }[] {
  const out: { category: string; items: PermissionSpec[] }[] = [];
  for (const p of PERMISSIONS) {
    let g = out.find((c) => c.category === p.category);
    if (!g) out.push((g = { category: p.category, items: [] }));
    g.items.push(p);
  }
  return out;
}

// ---------------------------------------------------------------------
// Equivalência com o RBAC anterior (fallback para quem está sem grupo e
// semente dos grupos legados da migration 030).
// ---------------------------------------------------------------------
const LEITURA: Permission[] = ["paineis", "operacoes", "cadastros", "metas"];

export const ROLE_DEFAULT_PERMISSIONS: Record<Role, Permission[]> = {
  admin: [...PERMISSION_KEYS],
  torre: [...LEITURA, "feeds", "vinculo", "agendamento", "programacao", "metas.editar", "dados", "dados.editar"],
  gestor: [...LEITURA, "feeds", "vinculo", "programacao", "metas.editar", "dados", "auditoria", "chamados.triagem"],
  frota: [...LEITURA, "cadastros.frota", "pii"],
  comercial: [...LEITURA, "cadastros.comercial", "metas.editar"],
  motorista: [...LEITURA],
};

/** Grupos que a migration 030 cria para o pedido da Brasil Novo (30/10/2026). */
export const DEFAULT_GROUPS = {
  acesso_total: { name: "Acesso total", baseRole: "admin" as Role, permissions: [...PERMISSION_KEYS] },
  gestor_frota: { name: "Gestor de frota", baseRole: "gestor" as Role, permissions: ["paineis", "vinculo"] as Permission[] },
  analista: { name: "Analista", baseRole: "torre" as Role, permissions: ["paineis", "vinculo", "agendamento"] as Permission[] },
};

export interface PermissionSubject {
  role: Role | string;
  /** Efetivas, já calculadas (vêm do banco a cada requisição). Ausente = sessão antiga → papel. */
  permissions?: readonly string[];
}

/** Calcula as permissões efetivas a partir do grupo e dos ajustes do usuário. */
export function effectivePermissions(input: {
  role: Role | string;
  groupPermissions?: readonly string[] | null;
  grant?: readonly string[] | null;
  revoke?: readonly string[] | null;
}): Permission[] {
  const base = input.groupPermissions ?? ROLE_DEFAULT_PERMISSIONS[input.role as Role] ?? [];
  const revoke = new Set(input.revoke ?? []);
  return normalizePermissions([...base, ...(input.grant ?? [])]).filter((p) => !revoke.has(p));
}

export function permissionsOf(subject: PermissionSubject | null | undefined): Permission[] {
  if (!subject) return [];
  if (subject.permissions) return normalizePermissions(subject.permissions);
  return ROLE_DEFAULT_PERMISSIONS[subject.role as Role] ?? [];
}

export function hasPermission(subject: PermissionSubject | null | undefined, perm: Permission): boolean {
  return permissionsOf(subject).includes(perm);
}

export const hasAnyPermission = (subject: PermissionSubject | null | undefined, ...perms: Permission[]) =>
  perms.some((p) => hasPermission(subject, p));

// ---------------------------------------------------------------------
// Áreas (páginas) → permissão exigida. Qualquer uma da lista basta.
// `null` = toda pessoa autenticada.
// ---------------------------------------------------------------------
export const AREA_PERMISSIONS: Record<string, Permission[] | null> = {
  inicio: null,
  dashboard: ["paineis"],
  paineis: ["paineis"],
  vinculo: ["vinculo"],
  operacoes: ["operacoes"],
  programacao: ["programacao"],
  dados: ["dados"],
  inconsistencias: ["metas", "dados"],
  feeds: ["feeds"],
  cadastros: ["cadastros"],
  metas: ["metas"],
  insights: ["metas"],
  explorar: ["metas"],
  agente: ["metas"],
  atualizacoes: null,
  chamados: null,
  auditoria: ["auditoria"],
  admin: ["usuarios"],
  entrega: ["sistema"],
};

export function canAreaWith(subject: PermissionSubject | null | undefined, area: string): boolean {
  if (!subject) return false;
  if (!(area in AREA_PERMISSIONS)) return false;
  const req = AREA_PERMISSIONS[area];
  return req === null || hasAnyPermission(subject, ...req);
}

// Escrita por entidade do catálogo → permissão exigida (qualquer uma basta).
export const ENTITY_WRITE_PERMISSIONS: Record<string, Permission[]> = {
  frota: ["cadastros.frota"],
  pbtc: ["cadastros.frota"],
  curso_autor: ["cadastros.frota"],
  exame_periodico: ["cadastros.frota"],
  clientes: ["cadastros.comercial"],
  tarifas: ["cadastros.comercial"],
  // Regiões: comercial e torre já escreviam aqui antes da D-27.
  regioes: ["cadastros.comercial", "agendamento"],
  regioes_usiminas: ["cadastros.comercial", "agendamento"],
  data_programacao: ["agendamento"],
  viagem_vega: ["agendamento"],
};

export function canWriteEntityWith(subject: PermissionSubject | null | undefined, entity: string): boolean {
  return hasAnyPermission(subject, ...(ENTITY_WRITE_PERMISSIONS[entity] ?? []));
}

// ---------------------------------------------------------------------
// Schemas da gestão de usuários e grupos (compartilhados front/back).
// ---------------------------------------------------------------------
const permList = z.array(z.string()).transform((l) => normalizePermissions(l));

export const groupInputSchema = z.object({
  name: z.string().trim().min(2, "Nome do grupo muito curto").max(80),
  description: z.string().trim().max(300).default(""),
  permissions: permList,
});
export type GroupInput = z.infer<typeof groupInputSchema>;

export const userInputSchema = z.object({
  email: z.string().trim().toLowerCase().email("E-mail inválido"),
  name: z.string().trim().min(2, "Informe o nome").max(120),
  cargo: z.string().trim().max(120).default(""),
  groupId: z.number().int().positive().nullable(),
  grant: permList.default([]),
  revoke: permList.default([]),
  active: z.boolean().default(true),
});
export type UserInput = z.infer<typeof userInputSchema>;

export const acceptInviteSchema = z.object({
  token: z.string().min(20).max(200),
  // Mesma política da troca de senha e do autocadastro.
  password: senhaZod,
});

/** Linha de usuário na tela de administração. */
export interface ManagedUser {
  id: number;
  email: string;
  name: string;
  cargo: string | null;
  role: Role;
  active: boolean;
  groupId: number | null;
  groupName: string | null;
  grant: Permission[];
  revoke: Permission[];
  permissions: Permission[];
  mustChangePassword: boolean;
  /** Convite pendente (senha ainda não definida pela pessoa). */
  invitePending: boolean;
  inviteExpiresAt: string | null;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface PermissionGroup {
  id: number;
  slug: string | null;
  name: string;
  description: string;
  baseRole: Role;
  permissions: Permission[];
  system: boolean;
  userCount: number;
}
